import { FC, useEffect, useRef } from 'react'
import type { ProductVariant } from '@commerce/types/product'
import Slider from 'react-slick'
import 'slick-carousel/slick/slick.css'
//import 'slick-carousel/slick/slick-theme.css'
import Image from 'next/image'
import { hasVideo } from './helpers'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faRotate } from '@fortawesome/free-solid-svg-icons'
import useWindowDimensions from 'hooks/useWindowDimensions'
import cn from 'clsx'
import { imageLoader } from '@utils/image-loader'

interface Props {
  productVariant: ProductVariant
  currentSlide: number
}

const ProductSlider: FC<Props> = ({ productVariant, currentSlide }) => {
  const sliderRef = useRef<Slider>(null)
  const { height } = useWindowDimensions()
  const placeholderImg = '/product-img-placeholder.svg'

  useEffect(() => {
    sliderRef.current?.slickGoTo(currentSlide)
  }, [currentSlide])

  const ArrowRight = () => {
    return (
      <svg
        viewBox="0 0 7 11"
        xmlns="http://www.w3.org/2000/svg"
        className="cursor-pointer absolute top-[350px] -right-3 z-1 w-[14px] h-[22px] fill-transparent"
        onClick={() => sliderRef.current?.slickNext()}
      >
        <path d="M1 10L6 5.54471L1 1" stroke="var(--secondary)" />
      </svg>
    )
  }

  const ArrowLeft = () => {
    return (
      <svg
        viewBox="0 0 7 11"
        xmlns="http://www.w3.org/2000/svg"
        className="transform rotate-180 cursor-pointer absolute top-[350px] -left-3 z-1 w-[14px] h-[22px] fill-transparent"
        onClick={() => sliderRef.current?.slickPrev()}
      >
        <path d="M1 10L6 5.54471L1 1" stroke="var(--secondary)" />
      </svg>
    )
  }

  const settings = {
    customPaging: function (i: number) {
      if (hasVideo(productVariant)) {
        if (i > 0) {
          return (
            <Image
              loader={imageLoader}
              src={productVariant.images[i - 1].url}
              alt="image"
              width={116}
              height={116}
            />
          )
        } else {
          return (
            <div className="relative w-full h-full">
              <Image
              loader={imageLoader}
                src={productVariant.images[i].url}
                alt="image"
                width={116}
                height={116}
              />
              <FontAwesomeIcon
                icon={faRotate}
                className="w-4 h-4 absolute top-2 right-2 text-accent-9"
              />
            </div>
          )
        }
      } else {
        return (
          <Image
            loader={imageLoader}
            src={productVariant.images[i].url}
            alt="image"
            width={116}
            height={116}
          />
        )
      }
    },
    dots: true,
    dotsClass: 'product-slick-dots !mt-[calc(((100vh-700px)/2)-100px)] z-10',
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: false,
    nextArrow: <ArrowRight />,
    prevArrow: <ArrowLeft />,
    adaptiveHeight: true,
  }
  return (
    <div className="w-full h-full text-center">
      <Slider ref={sliderRef} {...settings}>
        {productVariant.videos &&
          productVariant.videos.length > 0 &&
          (productVariant.videos[0].local_url ||
            productVariant.videos[0].alternative_url) && (
            <div className="!flex !justify-center">
              <video
                src={
                  productVariant.videos[0].local_url ||
                  productVariant.videos[0].alternative_url ||
                  ''
                }
                width={700}
                height={700}
                muted
                autoPlay
                loop
                playsInline
                className="w-[700px] h-[700px]"
              >
                <source
                  src={
                    productVariant.videos[0].local_url ||
                    productVariant.videos[0].alternative_url ||
                    ''
                  }
                  type="video/mp4"
                />
              </video>
            </div>
          )}
        {productVariant.images.map((image) => (
          <div key={image.url}>
            <Image
              loader={imageLoader}
              src={image.url || placeholderImg}
              alt={image.alt || 'Product Variant Image'}
              width={700}
              height={700}
              className={cn({
                '!min-h-[calc(100%-50px)] !min-w-[calc(100%-50px)]':
                  height && height < 950,
              })}
            />
          </div>
        ))}
      </Slider>
    </div>
  )
}

export default ProductSlider
